import * as React from "react";
import { useTerms } from "@/hooks/useTerms";
import { Link } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Card,
  CardContent,
  Input,
  DataTable,
  DataTablePagination,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DataTableScroll,
  DataTableEmpty,
  DataTableSkeleton,
  DataTableFooter,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Badge,
  StatCard,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ArchiveRestore,
  Clock,
  Download,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import type {
  AdmissionEnquiry,
  AdmissionFunnelStage,
  EnquiryListFilters,
  EnquiryListResponse,
} from "@eduviaos/shared";
import { useEnquiries, useFunnel, useRestoreEnquiry } from "@/hooks/useAdmissions";
import { useAuth } from "@/auth/AuthProvider";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { AdmissionsSubNav } from "@/components/AdmissionsSubNav";
import { api } from "@/lib/api";
import { ExportCsvErrorBanner } from "@/components/ExportCsvErrorBanner";
import { useCsvExport } from "@/hooks/useCsvExport";
import { ADMISSIONS_TRASH_CSV_COLUMNS, mapTrashEnquiryToCsvRow } from "@/lib/admissionsCsv";

/**
 * AdmissionsTrashPage — Phase D4.
 *
 * Soft-deleted enquiries view. Backed by
 * `GET /admissions/enquiries?only_deleted=true` (Phase D4 BE addition)
 * which guarantees the result set is purely the bin — no client-side
 * filtering required.
 *
 * UX principles:
 *   - Make restore the dominant action — the IA's most common reason
 *     to land here is "I deleted that one by mistake". Per-row Restore
 *     button + (in a follow-up) bulk-restore.
 *   - Auto-purge countdown is visible on every row so the IA knows
 *     when each enquiry will be permanently removed (BE policy: 30
 *     days from delete).
 *   - Iron Rule #16 — DataTable primitive, sticky header, right-aligned
 *     tabular nums for the countdown column, hover highlight only.
 */

// Phase E1.f — default rows-per-page; user can override via the
// shared `<DataTablePagination>` selector (persisted per-user via
// localStorage scope "admissions-trash").
const DEFAULT_PAGE_SIZE = 10;
/** Compliance policy: soft-deleted rows are purged after this many
 * days. Mirrors `eduviaos-docs/trd/13-observability-sla.md` §retention.
 * Surface this to the IA as a countdown so deletes don't surprise. */
const PURGE_DAYS = 30;

export function AdmissionsTrashPage() {
  const { user } = useAuth();
  const { ct } = useTerms();
  const tenantSlug = user?.tenant_id ?? null;
  const [qInput, setQInput] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "admissions-trash",
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });
  const [restoringId, setRestoringId] = React.useState<string | null>(null);

  const filters: EnquiryListFilters = React.useMemo(
    () => ({
      only_deleted: true,
      q: qInput.trim() || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [qInput, page, pageSize],
  );

  const { data, isLoading, isFetching, refetch, isError } = useEnquiries(filters);
  // Funnel powers the human-readable stage label in the CSV's `Stage`
  // column (matching the on-screen "Stage at delete" badge). It is
  // cheap (single fetch, react-query cached) and shared with the active
  // Admissions page, so adding the dependency here is free.
  const { data: funnel } = useFunnel();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  // `<DataTablePagination>` derives totalPages internally; the page
  // only needs the "X–Y of Z" footer string below.
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  // Group counts for the stat strip — derived from the visible page so
  // they update instantly with filters. Per-tenant hard counts come
  // from `total` which remains accurate across pages.
  const purgingSoonCount = items.filter((e) => purgeDaysLeft(e.deleted_at) <= 7).length;

  // ── Phase D2.b — CSV export ──────────────────────────────────────────────
  // Mirrors D2.a (active admissions export): iterates the same
  // `/admissions/enquiries` endpoint in 200-row chunks but with the
  // `only_deleted=true` flag flipped on. Honours the active `q`
  // toolbar filter server-side so the download matches the user's
  // on-screen view. Output filename:
  // `admissions-trash-<tenant_slug>-<YYYY-MM-DD>.csv`.
  //
  // Two extra compliance columns ride on the right edge of the row:
  // `Deleted at` (raw ISO 8601) and `Days until auto-purge` (derived
  // integer). The countdown is computed once at click-time and
  // applied to every row so the file is internally consistent — a
  // download that takes 2s won't see one row claim "5 days left" and
  // another "4 days left" because the clock ticked mid-fetch.
  // Phase D2.f — CSV export wiring delegated to the shared
  // `useCsvExport` primitive. The hook pins `now` once at click-time
  // and threads it through `buildRows(now)` so the retention
  // countdown column (`Days until auto-purge`) and the filename both
  // see the same instant — no off-by-one drift between row 1 and row
  // 50,000 of a large export.
  const csvExport = useCsvExport({
    filenamePrefix: "admissions-trash",
    tenantSlug,
    disabled: total === 0 && !isLoading,
    formatError: (err) =>
      err instanceof Error && err.message
        ? `Couldn't export the admissions trash as CSV: ${err.message}`
        : "Couldn't export the admissions trash as CSV.",
    buildRows: async (now) => {
      const collected: AdmissionEnquiry[] = [];
      const CHUNK = 200;
      const HARD_CAP = 50_000;
      let offset = 0;
      let expectedTotal: number | null = null;
      const trimmedQ = qInput.trim();
      while (collected.length < HARD_CAP) {
        const search = new URLSearchParams();
        search.set("only_deleted", "true");
        if (trimmedQ) search.set("q", trimmedQ);
        search.set("limit", String(CHUNK));
        search.set("offset", String(offset));
        const chunk = await api.get<EnquiryListResponse>(
          `/admissions/enquiries?${search.toString()}`,
        );
        collected.push(...chunk.items);
        if (expectedTotal === null) expectedTotal = chunk.total;
        offset += chunk.items.length;
        if (chunk.items.length === 0) break;
        if (chunk.items.length < CHUNK) break;
        if (expectedTotal !== null && collected.length >= expectedTotal) break;
      }

      const stageLookup = new Map<string, AdmissionFunnelStage>();
      if (funnel) for (const s of funnel.stages) stageLookup.set(s.stage_key, s);

      return {
        columns: ADMISSIONS_TRASH_CSV_COLUMNS,
        rows: collected.map((enquiry) => mapTrashEnquiryToCsvRow(enquiry, stageLookup, now)),
      };
    },
  });

  const exportDisabled = csvExport.isExporting || (total === 0 && !isLoading);

  return (
    <>
      <AdmissionsSubNav />
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Trash</PageHeaderHeading>
            <PageHeaderDescription>
              Recently deleted enquiries — restore within {PURGE_DAYS} days, after which they are
              permanently purged for compliance.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh trash"
            >
              <RefreshCw
                className={cn("size-4", isFetching && "animate-spin")}
                aria-hidden="true"
              />
              Refresh
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<Trash2 className="size-4" aria-hidden="true" />}
          label="In bin"
          value={total.toLocaleString()}
          caption="Recoverable enquiries"
          tone={total > 0 ? "blue" : "neutral"}
        />
        <StatCard
          icon={<Clock className="size-4" aria-hidden="true" />}
          label="Purging within 7 days"
          value={purgingSoonCount.toLocaleString()}
          caption={
            purgingSoonCount > 0
              ? "Restore now or accept permanent removal"
              : "Nothing on the chopping block"
          }
          tone={purgingSoonCount > 0 ? "amber" : "neutral"}
        />
        <StatCard
          icon={<ArchiveRestore className="size-4" aria-hidden="true" />}
          label="Retention policy"
          value={`${PURGE_DAYS} days`}
          caption="From soft-delete to permanent purge"
          tone="violet"
        />
      </div>

      <ExportCsvErrorBanner
        variant="inline"
        testid="admissions-trash-export-error"
        error={csvExport.exportError}
        onDismiss={csvExport.dismissError}
        className="mt-4"
      />

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
              aria-hidden="true"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => {
                setQInput(e.target.value);
                setPage(1);
              }}
              placeholder={ct("Search name, parent, email, phone…")}
              className="pl-8"
              aria-label="Search trash"
              data-testid="trash-search"
            />
          </div>
          {qInput && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQInput("");
                setPage(1);
              }}
            >
              <X className="size-3.5" aria-hidden="true" />
              Clear
            </Button>
          )}
          <DataTableToolbarSpacer />
          <Button
            variant="ghost"
            size="sm"
            onClick={csvExport.handleExport}
            disabled={exportDisabled}
            aria-label="Download admissions trash as CSV"
            data-testid="admissions-trash-export-csv"
          >
            <Download
              aria-hidden="true"
              className={cn("size-4", csvExport.isExporting && "animate-pulse")}
            />
            {csvExport.isExporting ? "Exporting…" : "Download CSV"}
          </Button>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Student</TableHead>
                <TableHead className="w-[24%]">Contact</TableHead>
                <TableHead className="w-[14%]">Stage at delete</TableHead>
                <TableHead className="w-[16%] text-right">Deleted</TableHead>
                <TableHead className="w-[10%] text-right">Auto-purge</TableHead>
                <TableHead className="w-[8%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={6} columns={6} />
            ) : (
              <TableBody>
                {items.map((e) => (
                  <TrashRow
                    key={e.id}
                    enquiry={e}
                    restoringId={restoringId}
                    onRestoreStart={(id) => setRestoringId(id)}
                    onRestoreSuccess={(id, name) => {
                      setRestoringId(null);
                      toast.success(`Restored "${name}" — it's back in the active funnel.`);
                      // Force a refetch so total + page rotate correctly.
                      void refetch();
                      void id;
                    }}
                    onRestoreError={(message) => {
                      setRestoringId(null);
                      toast.error(message);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 && (
          <DataTableEmpty
            icon={<Trash2 aria-hidden="true" />}
            title={
              isError ? "Couldn't load the trash" : qInput ? "No matches" : "Nothing in the bin"
            }
            description={
              isError
                ? "Try refreshing — if it keeps happening, check your network."
                : qInput
                  ? "Try a different search or clear the filter."
                  : "Anything you delete from the funnel will appear here for 30 days."
            }
            action={
              isError ? (
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  <RefreshCw className="size-4" aria-hidden="true" />
                  Retry
                </Button>
              ) : qInput ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setQInput("");
                    setPage(1);
                  }}
                >
                  <X className="size-3.5" aria-hidden="true" />
                  Clear search
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link to="/admissions">Open enquiries</Link>
                </Button>
              )
            }
          />
        )}

        {!isLoading && total > 0 && (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {start.toLocaleString()}–{end.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              page={page}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(1, next))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
              testIdPrefix="admissions-trash-pagination"
            />
          </DataTableFooter>
        )}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">Compliance note:</strong> deleted enquiries are kept
          for {PURGE_DAYS} days for auditing and accidental- deletion recovery, then permanently
          purged. Restored rows return to their original stage with the full activity log intact.
        </CardContent>
      </Card>
    </>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────────

interface TrashRowProps {
  enquiry: AdmissionEnquiry;
  restoringId: string | null;
  onRestoreStart: (id: string) => void;
  onRestoreSuccess: (id: string, name: string) => void;
  onRestoreError: (message: string) => void;
}

function TrashRow({
  enquiry,
  restoringId,
  onRestoreStart,
  onRestoreSuccess,
  onRestoreError,
}: TrashRowProps) {
  const restore = useRestoreEnquiry(enquiry.id);
  const daysLeft = purgeDaysLeft(enquiry.deleted_at);
  const purgingSoon = daysLeft <= 7;
  const restoring = restoringId === enquiry.id;

  const handleRestore = async () => {
    onRestoreStart(enquiry.id);
    try {
      await restore.mutateAsync();
      onRestoreSuccess(enquiry.id, enquiry.student_name);
    } catch (e) {
      onRestoreError(
        (e as { message?: string } | null)?.message ?? "Couldn't restore — try again.",
      );
    }
  };

  return (
    <TableRow data-testid={`trash-row-${enquiry.id}`}>
      <TableCell>
        <Link
          to={`/admissions/${enquiry.id}`}
          className="text-foreground hover:text-primary block truncate font-medium transition-colors"
        >
          {enquiry.student_name}
        </Link>
        {enquiry.parent_name && (
          <span className="text-muted-foreground block truncate text-xs">
            Parent: {enquiry.parent_name}
          </span>
        )}
      </TableCell>
      <TableCell>
        <div className="text-foreground text-sm">{enquiry.student_email ?? "—"}</div>
        <div className="text-muted-foreground text-xs">{enquiry.student_phone ?? "—"}</div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="font-normal">
          {enquiry.stage_key.replace(/_/g, " ")}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground text-right text-xs tabular-nums">
        {enquiry.deleted_at
          ? new Date(enquiry.deleted_at).toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "—"}
      </TableCell>
      <TableCell
        className={cn(
          "text-right text-xs tabular-nums",
          purgingSoon
            ? "font-semibold text-amber-600 dark:text-amber-400"
            : "text-muted-foreground",
        )}
      >
        {daysLeft <= 0 ? "Today" : `${daysLeft}d`}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleRestore()}
          disabled={restoring}
          data-testid={`trash-restore-${enquiry.id}`}
        >
          {restoring ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <ArchiveRestore className="size-3.5" aria-hidden="true" />
          )}
          Restore
        </Button>
      </TableCell>
    </TableRow>
  );
}

function purgeDaysLeft(deletedAt: string | null): number {
  if (!deletedAt) return PURGE_DAYS;
  const deletedAtMs = new Date(deletedAt).getTime();
  const elapsedDays = Math.floor((Date.now() - deletedAtMs) / 86_400_000);
  return Math.max(0, PURGE_DAYS - elapsedDays);
}
